"use client"
import { useConversationTimeFormatters } from "@/hooks/use-conversation-time-formatters"


import type { ReactNode } from "react"


import { cn } from "@/lib/utils"

export type ConversationBubbleTone = "customer" | "ai" | "staff" | "muted"

/**
 * One message, wherever a conversation is read.
 *
 * Body text stays `foreground`, which is what lets a light surface carry colour;
 * white text would force the fill back to 600-level saturation. Amber is absent
 * on purpose — it means «needs attention» elsewhere in the product. The customer
 * stays neutral so the coloured sides read as "us".
 */
export function ConversationBubble({
  align,
  tone,
  authorLabel,
  timestamp,
  timezone,
  children,
  trailer,
  className,
}: {
  align: "start" | "end"
  tone: ConversationBubbleTone
  authorLabel: string
  timestamp: string
  timezone: string
  children: ReactNode
  /** Delivery ticks, badges, log links — anything that belongs to the message but not inside it. */
  trailer?: ReactNode
  /** Width constraint of the calling surface; the two inboxes read at different widths. */
  className?: string
}) {
  const { formatFullDateTime, formatClockTime } = useConversationTimeFormatters()

  const styles = TONE_STYLES[tone]

  return (
    <div
      className={cn(
        "flex min-w-0 flex-col gap-0.5",
        align === "end" ? "ml-auto items-end" : "items-start",
        className,
      )}
    >
      {/* `max-w-full` is load-bearing: the wrapper is `items-start`, so this card is
          sized to its content, and content with no wrap opportunity — a mail's `<pre>`,
          a long URL — would size it past the wrapper's cap and scroll the whole
          transcript sideways. The cap belongs on the thing being sized. */}
      <div className={cn("min-w-0 max-w-full rounded-lg border px-3 py-2 text-sm break-words", styles.surface)}>
        <div className="mb-1 flex items-baseline justify-between gap-3 text-[11px]">
          <span className={cn("truncate font-medium", styles.author)}>{authorLabel}</span>
          <time
            dateTime={timestamp}
            title={formatFullDateTime(timestamp, timezone)}
            className="flex-shrink-0 text-muted-foreground"
          >
            {formatClockTime(timestamp, timezone)}
          </time>
        </div>
        {children}
      </div>
      {trailer && <div className="flex flex-wrap items-center gap-1 px-1">{trailer}</div>}
    </div>
  )
}

const TONE_STYLES: Record<ConversationBubbleTone, { surface: string; author: string }> = {
  customer: {
    surface: "bg-muted border-border",
    author: "text-foreground",
  },
  ai: {
    surface: "bg-blue-100 border-blue-200 dark:bg-blue-900/50 dark:border-blue-800",
    author: "text-blue-700 dark:text-blue-300",
  },
  staff: {
    surface: "bg-emerald-100 border-emerald-200 dark:bg-emerald-900/50 dark:border-emerald-800",
    author: "text-emerald-700 dark:text-emerald-300",
  },
  muted: {
    surface: "bg-muted border-dashed border-border",
    author: "text-muted-foreground",
  },
}
